import { px } from "@/components/tool-kit/charts/marks";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * Small SVG pieces the diagrams share: an arrow, and a block of text lines.
 *
 * The arrowhead is a polygon drawn at the end of the line rather than an SVG `<marker>`:
 * a marker needs an id that is unique on the page and survives serialisation, and a few
 * PNG rasterisers drop markers altogether. A polygon is just a shape.
 */

type Point = readonly [number, number];

function polyline(points: readonly Point[]): string {
  return points.map(([x, y]) => `${px(x)},${px(y)}`).join(" ");
}

/** A line through `points` ending in a filled arrowhead at the last point. */
export function Arrow({
  points,
  color = CHART_INK.text,
  width = 1.2,
  head = 7,
  dashed = false,
}: {
  points: readonly Point[];
  color?: string;
  width?: number;
  head?: number;
  dashed?: boolean;
}) {
  const end = points.at(-1);
  const before = points.at(-2);
  if (!(end && before)) {
    return null;
  }
  const angle = Math.atan2(end[1] - before[1], end[0] - before[0]);
  const length = Math.hypot(end[0] - before[0], end[1] - before[1]);
  const back = Math.min(head, length);
  const base: Point = [
    end[0] - back * Math.cos(angle),
    end[1] - back * Math.sin(angle),
  ];
  const wing = head * 0.45;
  const left: Point = [
    base[0] + wing * Math.sin(angle),
    base[1] - wing * Math.cos(angle),
  ];
  const right: Point = [
    base[0] - wing * Math.sin(angle),
    base[1] + wing * Math.cos(angle),
  ];
  return (
    <g>
      <polyline
        fill="none"
        points={polyline([...points.slice(0, -1), base])}
        stroke={color}
        strokeDasharray={dashed ? "4 3" : undefined}
        strokeLinejoin="round"
        strokeWidth={width}
      />
      <polygon fill={color} points={polyline([end, left, right])} />
    </g>
  );
}

/**
 * Lines of text set one under another, `top` being the top of the first line. Each
 * line is centred on its slot with the same 0.35 em shift the chart axes use, which
 * needs no `dominant-baseline` (unsupported by some PNG rasterisers).
 */
export function TextLines({
  lines,
  tops,
  x,
  top,
  fontSize,
  lineHeight,
  fill = CHART_INK.text,
  anchor = "middle",
  bold = false,
}: {
  lines: readonly string[];
  /** Each line's top relative to `top`; evenly spaced by `lineHeight` when absent. */
  tops?: readonly number[];
  x: number;
  top: number;
  fontSize: number;
  lineHeight: number;
  fill?: string;
  anchor?: "start" | "middle" | "end";
  bold?: boolean;
}) {
  return (
    <g>
      {lines.map((line, index) => (
        <text
          fill={fill}
          fontSize={fontSize}
          fontWeight={bold ? "bold" : undefined}
          // Lines of one block: position is their identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional lines
          key={index}
          textAnchor={anchor}
          x={px(x)}
          y={px(
            top +
              (tops?.[index] ?? index * lineHeight) +
              lineHeight / 2 +
              fontSize * 0.35
          )}
        >
          {line}
        </text>
      ))}
    </g>
  );
}
